import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Upload, Sparkles, CheckCircle2, AlertTriangle, X, RefreshCw,
  FileText, Save, Layers, Link2,
} from "lucide-react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Button, Card, Field, Input, Select, Spinner } from "@/components/ui";
import { StickyBottomBar } from "@/components/layout/StickyBottomBar";
import type { Json } from "@/lib/database.types";
import {
  ACCEPTED_PHOTO_MIMES, licencePhotoPath, CATEGORY_DISPLAY, CATEGORY_ORDER, fmtDate,
} from "./licenceHelpers";

// =============================================================================
// BulkLicenceIntakeScreen — /hr/licences/:employeeId/bulk (on-behalf, manager+)
//                           /licences/bulk               (self)
// =============================================================================
// James, 15 Sep 2026: "Wizard is good but maybe a bulk option would be nice,
// not sure how to get around front back issues but there must be a way. I have
// a bunch of PDFs to load in for new employees they send me a folder with
// allllll their tickets and inductions." And on the subbie side: "look at
// updating that worker to take bulk uploads instead of one at a time ... build
// the table like AP and final review and worker populates and then we approve."
//
// The worker already does the hard part one file at a time: /licences/ai-extract
// reads a photo or PDF, matches it to a licence_type, pulls number/dates/state,
// and — the bit that solves front/back — reports which SIDE it was looking at
// and the holder's name. So bulk is a front-end job:
//
//   1. Drop a folder's worth of files. Each becomes a row.
//   2. Rows upload + extract three at a time (same storage path shape and the
//      same endpoint the single wizard uses — nothing new on the worker).
//   3. A file the AI calls a BACK is not a licence: it attaches itself to the
//      one front row of the same type that has no back yet, or waits for the
//      reviewer to pick which row it belongs to.
//   4. Review table: every field editable, low-confidence and UNKNOWN rows
//      flagged, "name on card ≠ this employee" flagged, "same type as another
//      row" flagged (app.add_licence supersedes the earlier one), "renews an
//      existing licence" shown, and a card that is ALREADY LOADED (same type
//      AND same number as an active licence) is skipped by default — without
//      that, re-dropping a folder would silently supersede live rows with
//      identical copies. Two more skip-by-default guards, both from James's
//      first live run (15 Sep): a card whose EXPIRY IS EARLIER than the one
//      already loaded (an old CPR dropped over a newer one), and a card whose
//      NAME doesn't match the employee (the wrong person's folder). Saving is
//      never destructive — add_licence archives, it doesn't delete — but a
//      superseded row is still a mess to undo. "Load anyway" overrides per row.
//   5. Save all → app.add_licence per row, sequentially (it archives the old
//      active row of the same type, so order matters), each row reporting its
//      own result. Nothing is written until Save all.
//
// The single wizard (NewLicenceScreen) is untouched and still the right tool
// for one card on a phone.
// =============================================================================

const AU_STATES = ["QLD", "NSW", "VIC", "TAS", "SA", "WA", "NT", "ACT"] as const;
const CONCURRENCY = 3;
const MAX_BYTES   = 10 * 1024 * 1024;

interface LicenceType {
  licence_type_id: string;
  code: string;
  display_name: string;
  category: string;
  requires_back_photo: boolean;
  requires_renewal: boolean;
}

interface ExistingLicence {
  licence_type_id: string;
  expiry_date: string | null;
  licence_number: string | null;
}

type RowStatus =
  | "queued" | "uploading" | "reading"   // pipeline
  | "ready" | "error"                    // read outcome
  | "back"                               // this file is the BACK of another row
  | "saving" | "saved" | "save_error";   // Save all outcome

interface Row {
  id: string;
  file: File;
  previewUrl: string | null;
  storagePath: string | null;
  status: RowStatus;
  error: string | null;
  // AI read
  confidence: number | null;
  side: string | null;
  holderName: string | null;
  aiNote: string | null;
  // editable
  licenceTypeId: string;
  licenceNumber: string;
  issueDate: string;
  expiryDate: string;
  stateIssued: string;
  // back-side plumbing
  backOf: string | null;          // set on a BACK row: the front row it belongs to
  backPath: string | null;        // set on a FRONT row: the attached back's storage path
  savedLicenceId: string | null;
  forceLoad: boolean;             // reviewer said "load anyway" on an already-loaded card
  bothSides: boolean;             // this one file shows front AND back (a scanned PDF, a composite photo)
}

const isBackRowStatus = (s: RowStatus) => s === "back";

function newId() {
  return (typeof crypto !== "undefined" && "randomUUID" in crypto)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Loose "is this the same person" — first + last token, case/punctuation-blind. */
function nameLooksLike(cardName: string | null, first: string | null, last: string | null): boolean | null {
  if (!cardName || (!first && !last)) return null;
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z ]/g, " ").split(/\s+/).filter(Boolean);
  const card = norm(cardName);
  const f = first ? norm(first)[0] : null;
  const l = last  ? norm(last).slice(-1)[0] : null;
  const hasF = !f || card.includes(f);
  const hasL = !l || card.includes(l);
  return hasF && hasL;
}

export default function BulkLicenceIntakeScreen() {
  const user     = useRequireAuth();
  const navigate = useNavigate();
  const qc       = useQueryClient();

  const { employeeId: routeEmployeeId } = useParams<{ employeeId?: string }>();
  const targetEmployeeId = routeEmployeeId ?? user?.employee_id ?? null;
  const isOnBehalf       = !!routeEmployeeId && routeEmployeeId !== user?.employee_id;
  const canActOnBehalf   = hasRole(user, "manager");

  // ---- hooks first, always (early returns below) ---------------------------
  const [rows, setRows] = useState<Row[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState<{ saved: number; failed: number } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const rowsRef   = useRef<Row[]>([]);
  rowsRef.current = rows;
  const inFlight  = useRef(0);

  const { data: targetEmployee } = useQuery({
    queryKey: ["bulk_licence_target_employee", targetEmployeeId],
    enabled: !!targetEmployeeId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_employees")
        .select("employee_id, first_name, last_name, email")
        .eq("employee_id", targetEmployeeId!)
        .maybeSingle();
      if (error) throw error;
      return data as { employee_id: string; first_name: string | null; last_name: string | null; email: string | null } | null;
    },
  });

  const { data: licenceTypes = [], isLoading: typesLoading } = useQuery({
    queryKey: ["licence_types_active_bulk"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("licence_types")
        .select("licence_type_id, code, display_name, category, requires_back_photo, requires_renewal")
        .eq("is_active", true)
        .order("sort_order")
        .order("display_name");
      if (error) throw error;
      return (data ?? []) as unknown as LicenceType[];
    },
  });

  const { data: existing = [] } = useQuery({
    queryKey: ["bulk_licence_existing", targetEmployeeId],
    enabled: !!targetEmployeeId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employee_licences")
        .select("licence_type_id, expiry_date, licence_number")
        .eq("employee_id", targetEmployeeId!)
        .eq("is_active", true);
      if (error) throw error;
      return (data ?? []) as unknown as ExistingLicence[];
    },
  });

  const typeById = useMemo(() => new Map(licenceTypes.map(t => [t.licence_type_id, t])), [licenceTypes]);
  const typesGrouped = useMemo(() => {
    const g = new Map<string, LicenceType[]>();
    for (const t of licenceTypes) { if (!g.has(t.category)) g.set(t.category, []); g.get(t.category)!.push(t); }
    return g;
  }, [licenceTypes]);
  const existingByType = useMemo(() => new Map(existing.map(e => [e.licence_type_id, e])), [existing]);
  const normNo = (v: string | null | undefined) => (v ?? "").replace(/[\s\-\/.]/g, "").toUpperCase();
  /** Same type AND same number as a licence already active on this person = the card is already in Ops. */
  const alreadyLoaded = (r: Row): ExistingLicence | null => {
    if (!r.licenceTypeId) return null;
    const ex = existingByType.get(r.licenceTypeId);
    if (!ex) return null;
    const a = normNo(r.licenceNumber), b = normNo(ex.licence_number);
    return a && b && a === b ? ex : null;
  };
  /** Same type, and what is already loaded expires LATER than this card — saving would replace newer with older. */
  const olderThanLoaded = (r: Row): ExistingLicence | null => {
    if (!r.licenceTypeId || !r.expiryDate) return null;
    const ex = existingByType.get(r.licenceTypeId);
    if (!ex?.expiry_date) return null;
    return ex.expiry_date > r.expiryDate ? ex : null;
  };
  const nameMismatch = (r: Row): boolean =>
    nameLooksLike(r.holderName, targetEmployee?.first_name ?? null, targetEmployee?.last_name ?? null) === false;
  /** Why a row is held back unless the reviewer says "load anyway". */
  const holdReason = (r: Row): string | null => {
    if (r.forceLoad) return null;
    const dupe = alreadyLoaded(r);
    if (dupe) return `already loaded${dupe.expiry_date ? ` (exp ${fmtDate(dupe.expiry_date)})` : ""}`;
    const older = olderThanLoaded(r);
    if (older) return `older than what's loaded (exp ${fmtDate(older.expiry_date!)})`;
    if (nameMismatch(r)) return `name on card: ${r.holderName}`;
    return null;
  };

  // Object URLs die with the rows.
  useEffect(() => () => { rowsRef.current.forEach(r => r.previewUrl && URL.revokeObjectURL(r.previewUrl)); }, []);

  // ---- the pipeline: upload → extract, three at a time ---------------------
  const patch = (id: string, p: Partial<Row>) =>
    setRows(prev => prev.map(r => (r.id === id ? { ...r, ...p } : r)));

  async function processRow(row: Row) {
    if (!targetEmployeeId) return;
    try {
      patch(row.id, { status: "uploading", error: null });
      // Uploaded as "front" — we don't know the side until the AI has looked.
      // A back gets moved to a back_ name when it attaches (best effort).
      const path = row.storagePath ?? licencePhotoPath(targetEmployeeId, row.id, "front", row.file);
      if (!row.storagePath) {
        const { error: upErr } = await supabase.storage
          .from("licence-documents")
          .upload(path, row.file, { contentType: row.file.type, upsert: false });
        if (upErr) throw new Error(`Upload failed: ${upErr.message}`);
        patch(row.id, { storagePath: path });
      }

      patch(row.id, { status: "reading" });
      const res = await apiFetch("/licences/ai-extract", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ storage_path: path }),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`Couldn't read it (${res.status}): ${txt.slice(0, 300)}`);
      }
      const ai = await res.json() as {
        confidence: number;
        licence_type_id: string | null;
        extracted: {
          licence_number?: string | null; issue_date?: string | null; expiry_date?: string | null;
          state_issued?: string | null; notes?: string | null; side?: string | null; holder_name?: string | null;
        };
      };

      const isBack = (ai.extracted?.side ?? "unknown") === "back";
      // 15 Sep 2026 — James: "Sometimes a PDF will be a scan of the front and
      // back of that card." The AI reports side: "both" for those; the record
      // then points the back at the same file, which is exactly where it is.
      const both = (ai.extracted?.side ?? "unknown") === "both";
      patch(row.id, {
        status: isBack ? "back" : "ready",
        bothSides: both,
        confidence: ai.confidence ?? null,
        side: ai.extracted?.side ?? null,
        holderName: ai.extracted?.holder_name ?? null,
        aiNote: ai.extracted?.notes ?? null,
        licenceTypeId: ai.licence_type_id ?? "",
        licenceNumber: ai.extracted?.licence_number ?? "",
        issueDate:  ai.extracted?.issue_date  ?? "",
        expiryDate: ai.extracted?.expiry_date ?? "",
        stateIssued: (ai.extracted?.state_issued ?? "").toUpperCase(),
      });

      // A back attaches itself when exactly one front of the same type has no back yet.
      if (isBack && ai.licence_type_id) {
        const fronts = rowsRef.current.filter(r =>
          r.id !== row.id && r.status === "ready" && r.licenceTypeId === ai.licence_type_id && !r.backPath);
        if (fronts.length === 1) attachBack(row.id, fronts[0].id, path);
      }
    } catch (e: any) {
      patch(row.id, { status: "error", error: e?.message ?? "Failed" });
    }
  }

  // Drain the queue whenever rows change.
  useEffect(() => {
    const queued = rows.filter(r => r.status === "queued");
    while (inFlight.current < CONCURRENCY && queued.length) {
      const next = queued.shift()!;
      inFlight.current += 1;
      patch(next.id, { status: "uploading" });
      processRow(next).finally(() => { inFlight.current -= 1; setRows(prev => [...prev]); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  function addFiles(list: FileList | File[]) {
    const accepted = ACCEPTED_PHOTO_MIMES.split(",");
    const fresh: Row[] = [];
    for (const f of Array.from(list)) {
      if (!accepted.includes(f.type)) continue;
      if (f.size > MAX_BYTES) continue;
      fresh.push({
        id: newId(), file: f,
        previewUrl: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
        storagePath: null, status: "queued", error: null,
        confidence: null, side: null, holderName: null, aiNote: null,
        licenceTypeId: "", licenceNumber: "", issueDate: "", expiryDate: "", stateIssued: "",
        backOf: null, backPath: null, savedLicenceId: null, forceLoad: false, bothSides: false,
      });
    }
    if (fresh.length) { setSummary(null); setRows(prev => [...prev, ...fresh]); }
  }

  function attachBack(backRowId: string, frontRowId: string, backPath: string) {
    setRows(prev => prev.map(r => {
      if (r.id === backRowId)  return { ...r, backOf: frontRowId, status: "back" };
      if (r.id === frontRowId) return { ...r, backPath };
      if (r.backPath === backPath && r.id !== frontRowId) return { ...r, backPath: null }; // moved from another front
      return r;
    }));
  }
  function detachBack(backRowId: string) {
    setRows(prev => {
      const back = prev.find(r => r.id === backRowId);
      return prev.map(r => {
        if (r.id === backRowId) return { ...r, backOf: null };
        if (back && r.backPath === back.storagePath) return { ...r, backPath: null };
        return r;
      });
    });
  }
  function removeRow(id: string) {
    setRows(prev => {
      const gone = prev.find(r => r.id === id);
      if (gone?.previewUrl) URL.revokeObjectURL(gone.previewUrl);
      return prev
        .filter(r => r.id !== id)
        .map(r => (gone && r.backPath === gone.storagePath ? { ...r, backPath: null } : r))
        .map(r => (r.backOf === id ? { ...r, backOf: null } : r));
    });
  }
  function treatAsFront(id: string) {
    detachBack(id);
    patch(id, { status: "ready", side: "front" });
  }

  // ---- derived flags -------------------------------------------------------
  const fronts = rows.filter(r => r.status !== "back" && r.backOf === null);
  const typeCounts = useMemo(() => {
    const m = new Map<string, number>();
    fronts.forEach(r => { if (r.licenceTypeId) m.set(r.licenceTypeId, (m.get(r.licenceTypeId) ?? 0) + 1); });
    return m;
  }, [fronts]);
  const readyToSave = fronts.filter(r =>
    (r.status === "ready" || r.status === "save_error") && r.licenceTypeId && !holdReason(r));
  const skippedDupes = fronts.filter(r => r.status === "ready" && !!holdReason(r));
  const needAttention = fronts.filter(r => (r.status === "ready" && !r.licenceTypeId) || r.status === "error");
  const busy = rows.some(r => ["queued", "uploading", "reading"].includes(r.status));
  const unattachedBacks = rows.filter(r => r.status === "back" && !r.backOf);

  // ---- Save all -------------------------------------------------------------
  async function saveAll() {
    if (!targetEmployeeId || saving) return;
    setSaving(true);
    let saved = 0, failed = 0;
    for (const r of readyToSave) {
      patch(r.id, { status: "saving", error: null });
      try {
        const payload = {
          licence_type_id: r.licenceTypeId,
          licence_number:  r.licenceNumber.trim() || null,
          issue_date:      r.issueDate  || null,
          expiry_date:     r.expiryDate || null,
          state_issued:    r.stateIssued || null,
          front_photo_url: r.storagePath,
          back_photo_url:  r.backPath ?? (r.bothSides ? r.storagePath : null),
          notes:           r.holderName ? `Bulk intake · name on card: ${r.holderName}` : "Bulk intake",
          external_url:    null,
        };
        const { data, error } = await supabase
          .schema("app")
          .rpc("add_licence", { p_employee_id: targetEmployeeId, p_payload: payload as unknown as Json });
        if (error) throw error;
        patch(r.id, { status: "saved", savedLicenceId: (data as unknown as string) ?? null });
        saved += 1;
      } catch (e: any) {
        patch(r.id, { status: "save_error", error: e?.message ?? "Save failed" });
        failed += 1;
      }
    }
    setSaving(false);
    setSummary({ saved, failed });
    qc.invalidateQueries({ queryKey: ["my_licences"] });
    qc.invalidateQueries({ queryKey: ["hr_licences_all"] });
    qc.invalidateQueries({ queryKey: ["hr_licences_detail", targetEmployeeId] });
    qc.invalidateQueries({ queryKey: ["dashboard_pending_licences"] });
    qc.invalidateQueries({ queryKey: ["bulk_licence_existing", targetEmployeeId] });
  }

  // ---- gates (after every hook) --------------------------------------------
  if (!user) return null;
  if (isOnBehalf && !canActOnBehalf) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card><div className="p-5 text-[13px] text-danger">Adding licences on someone's behalf is a manager action.</div></Card>
      </div>
    );
  }
  if (!targetEmployeeId) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card><div className="p-5 text-[13px] text-danger">Your login isn't linked to an employee record. Ask a director.</div></Card>
      </div>
    );
  }
  if (typesLoading) {
    return <div className="min-h-[40vh] flex items-center justify-center"><Spinner size={20} /></div>;
  }

  const backLink   = isOnBehalf ? `/hr/licences/${targetEmployeeId}` : "/licences";
  const personName = targetEmployee ? `${targetEmployee.first_name ?? ""} ${targetEmployee.last_name ?? ""}`.trim() : null;
  const doneCount  = rows.filter(r => r.status === "saved").length;

  return (
    <div className="p-4 md:p-6 max-w-[1100px] mx-auto">
      <Link to={backLink} className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text mb-3">
        <ArrowLeft size={13} /> {isOnBehalf ? "Back to licence detail" : "Back to my licences"}
      </Link>

      <div className="mb-4">
        <h1 className="text-[22px] md:text-2xl font-semibold tracking-tight">
          Bulk add licences{personName ? ` for ${personName}` : ""}
        </h1>
        <p className="text-text-muted text-[13px] mt-1">
          Drop the whole folder — tickets, inductions, certs, front and back photos. The AI reads each one,
          you check the table, then save the lot.
        </p>
      </div>

      {isOnBehalf && targetEmployee && (
        <div className="mb-4 border border-accent/40 bg-accent/5 rounded-md px-3 py-2 text-[12px] text-text flex items-center gap-2">
          <Layers size={13} className="text-accent" />
          On-behalf of <span className="font-semibold">{personName}</span>{targetEmployee.email ? <span className="text-text-muted"> · {targetEmployee.email}</span> : null}
        </div>
      )}

      {/* Drop zone */}
      <Card className="mb-4">
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
          onClick={() => fileInput.current?.click()}
          className={`m-4 rounded-lg border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
            dragOver ? "border-accent bg-accent/5" : "border-line hover:border-line-light"}`}
        >
          <Upload size={22} className="mx-auto text-text-muted mb-2" />
          <div className="text-[14px] font-medium text-text">Drop files here or click to browse</div>
          <div className="text-[12px] text-text-muted mt-1">
            JPEG · PNG · WebP · PDF, up to 10 MB each. Select as many as you like — one file per card side.
          </div>
          <input ref={fileInput} type="file" multiple accept={ACCEPTED_PHOTO_MIMES} className="hidden"
                 onChange={e => { if (e.target.files) addFiles(e.target.files); e.currentTarget.value = ""; }} />
        </div>
        <div className="px-4 pb-4 -mt-1 flex items-center gap-2 text-[11px] text-text-muted">
          <Sparkles size={12} className="text-accent" />
          Reads {CONCURRENCY} at a time. A photo the AI recognises as a card's back is attached to its front automatically when there's exactly one match — otherwise you pick.
        </div>
      </Card>

      {/* Attention strip */}
      {rows.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 text-[12px]">
          <Chip tone="muted">{rows.length} file{rows.length === 1 ? "" : "s"}</Chip>
          {busy && <Chip tone="info"><Spinner size={11} /> reading…</Chip>}
          <Chip tone="ok">{readyToSave.length} ready</Chip>
          {needAttention.length > 0 && <Chip tone="warn"><AlertTriangle size={11} /> {needAttention.length} need attention</Chip>}
          {unattachedBacks.length > 0 && <Chip tone="warn"><Link2 size={11} /> {unattachedBacks.length} back side{unattachedBacks.length === 1 ? "" : "s"} not attached</Chip>}
          {skippedDupes.length > 0 && <Chip tone="warn">{skippedDupes.length} held back — see rows</Chip>}
          {doneCount > 0 && <Chip tone="ok"><CheckCircle2 size={11} /> {doneCount} saved</Chip>}
        </div>
      )}

      {/* Rows */}
      <div className="space-y-3">
        {rows.map((r, idx) => {
          const t = r.licenceTypeId ? typeById.get(r.licenceTypeId) : null;
          const dup = r.licenceTypeId && (typeCounts.get(r.licenceTypeId) ?? 0) > 1;
          const hold   = holdReason(r);
          const held   = !!hold && !isBackRowStatus(r.status) && r.status === "ready";
          const wouldHold = !r.forceLoad ? null : (alreadyLoaded(r) || olderThanLoaded(r) || nameMismatch(r));
          const renews = !alreadyLoaded(r) && !olderThanLoaded(r) && r.licenceTypeId ? existingByType.get(r.licenceTypeId) : null;
          const isBackRow = r.status === "back";
          const lowConf = r.confidence != null && r.confidence < 0.6;
          const attachedBack = !isBackRow && r.backPath ? rows.find(b => b.storagePath === r.backPath) : null;

          return (
            <Card key={r.id} className={
              r.status === "saved" ? "border-ok/40" :
              r.status === "error" || r.status === "save_error" ? "border-danger/40" :
              isBackRow ? "border-line bg-ink-raised/50" : ""}>
              <div className="p-3 md:p-4 flex gap-3 md:gap-4">
                {/* thumb */}
                <div className="shrink-0 w-[72px] h-[72px] md:w-[96px] md:h-[96px] rounded-md border border-line bg-ink-raised overflow-hidden flex items-center justify-center">
                  {r.previewUrl
                    ? <img src={r.previewUrl} alt="" className="w-full h-full object-cover" />
                    : <FileText size={22} className="text-text-dim" />}
                </div>

                <div className="min-w-0 flex-1">
                  {/* row header */}
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="min-w-0">
                      <div className="text-[12px] font-mono text-text-muted truncate max-w-[42ch]" title={r.file.name}>
                        {idx + 1}. {r.file.name}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        <StatusChip status={r.status} />
                        {r.confidence != null && !isBackRow && (
                          <Chip tone={lowConf ? "warn" : "muted"}>{Math.round(r.confidence * 100)}% sure</Chip>
                        )}
                        {!isBackRow && r.status === "ready" && !r.licenceTypeId && (
                          <Chip tone="warn">type not recognised — pick one</Chip>
                        )}
                        {dup && !isBackRow && <Chip tone="warn">same type as another row — later one supersedes</Chip>}
                        {renews && !isBackRow && (
                          <Chip tone="info">renews existing{renews.expiry_date ? ` (exp ${fmtDate(renews.expiry_date)})` : ""}</Chip>
                        )}
                        {held && (
                          <Chip tone="warn"><AlertTriangle size={11} /> {hold} — skipped</Chip>
                        )}
                        {!isBackRow && r.forceLoad && wouldHold && r.status !== "saved" && (
                          <Chip tone="warn">loading anyway — supersedes what's on file</Chip>
                        )}
                        {!isBackRow && r.status === "ready" && (held || (r.forceLoad && wouldHold)) && (
                          <button type="button" onClick={() => patch(r.id, { forceLoad: !r.forceLoad })}
                                  className="text-[11px] text-accent hover:underline">
                            {r.forceLoad ? "skip it" : "load anyway"}
                          </button>
                        )}
                        {attachedBack && <Chip tone="ok"><Link2 size={11} /> back attached</Chip>}
                        {!isBackRow && r.bothSides && !r.backPath && <Chip tone="ok">both sides in this file</Chip>}
                        {t?.requires_back_photo && !r.backPath && !r.bothSides && !isBackRow && (
                          <Chip tone="warn">needs a back — attach one, or tick "both sides"</Chip>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {(r.status === "error") && (
                        <button type="button" onClick={() => patch(r.id, { status: "queued" })}
                                className="p-1.5 rounded text-text-muted hover:text-text hover:bg-ink-hover" title="Read again">
                          <RefreshCw size={14} />
                        </button>
                      )}
                      {r.status !== "saving" && r.status !== "saved" && (
                        <button type="button" onClick={() => removeRow(r.id)}
                                className="p-1.5 rounded text-text-muted hover:text-danger hover:bg-ink-hover" title="Remove">
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  </div>

                  {r.error && <div className="mt-2 text-[12px] text-danger">{r.error}</div>}
                  {r.aiNote && !r.error && <div className="mt-2 text-[11px] text-text-muted italic">AI: {r.aiNote}</div>}

                  {/* BACK row: attach picker */}
                  {isBackRow && (
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-end">
                      <Field label="This is the back of">
                        <Select value={r.backOf ?? ""} onChange={e => {
                          const v = e.target.value;
                          if (!v) detachBack(r.id); else if (r.storagePath) attachBack(r.id, v, r.storagePath);
                        }}>
                          <option value="">— pick the front it belongs to —</option>
                          {fronts.filter(f => f.status !== "saved").map(f => {
                            const ft = f.licenceTypeId ? typeById.get(f.licenceTypeId) : null;
                            return <option key={f.id} value={f.id}>{rows.indexOf(f) + 1}. {ft?.display_name ?? f.file.name}{f.backPath && f.backPath !== r.storagePath ? " (has a back)" : ""}</option>;
                          })}
                        </Select>
                      </Field>
                      <Button variant="secondary" size="sm" onClick={() => treatAsFront(r.id)} title="The AI got the side wrong — treat this as its own licence">
                        It's a front
                      </Button>
                    </div>
                  )}

                  {/* FRONT row: editable fields */}
                  {!isBackRow && (r.status === "ready" || r.status === "saving" || r.status === "saved" || r.status === "save_error") && (
                    <div className="mt-3 grid grid-cols-2 md:grid-cols-6 gap-2">
                      <div className="col-span-2">
                        <Field label="Licence type">
                          <Select value={r.licenceTypeId} disabled={r.status === "saved"} onChange={e => patch(r.id, { licenceTypeId: e.target.value })}>
                            <option value="">— select —</option>
                            {CATEGORY_ORDER.filter(c => typesGrouped.has(c)).map(c => (
                              <optgroup key={c} label={CATEGORY_DISPLAY[c] ?? c}>
                                {typesGrouped.get(c)!.map(lt => <option key={lt.licence_type_id} value={lt.licence_type_id}>{lt.display_name}</option>)}
                              </optgroup>
                            ))}
                          </Select>
                        </Field>
                      </div>
                      <Field label="Number">
                        <Input mono value={r.licenceNumber} disabled={r.status === "saved"} onChange={e => patch(r.id, { licenceNumber: e.target.value })} />
                      </Field>
                      <Field label="Issued">
                        <Input type="date" value={r.issueDate} disabled={r.status === "saved"} onChange={e => patch(r.id, { issueDate: e.target.value })} />
                      </Field>
                      <Field label="Expiry">
                        <Input type="date" value={r.expiryDate} disabled={r.status === "saved"} onChange={e => patch(r.id, { expiryDate: e.target.value })} />
                      </Field>
                      <Field label="State">
                        <Select value={r.stateIssued} disabled={r.status === "saved"} onChange={e => patch(r.id, { stateIssued: e.target.value })}>
                          <option value="">—</option>
                          {AU_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                        </Select>
                      </Field>
                      {!r.backPath && (
                        <label className="col-span-2 md:col-span-6 inline-flex items-center gap-2 text-[12px] text-text-muted cursor-pointer select-none">
                          <input type="checkbox" checked={r.bothSides} disabled={r.status === "saved"}
                                 onChange={e => patch(r.id, { bothSides: e.target.checked })}
                                 className="accent-accent" />
                          This file shows both the front and the back of the card
                        </label>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {rows.length === 0 && (
        <div className="text-center text-[12px] text-text-muted py-6">
          Nothing here yet. One card at a time still lives at <Link to={isOnBehalf ? `/hr/licences/${targetEmployeeId}/new` : "/licences/new"} className="text-accent hover:underline">Add licence</Link>.
        </div>
      )}

      {/* Sticky action bar */}
      {rows.length > 0 && (
        <StickyBottomBar className="px-4 py-3">
          <div className="max-w-[1100px] mx-auto flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[12px] text-text-muted">
              {summary
                ? <span><span className="text-ok font-medium">{summary.saved} saved</span>{summary.failed > 0 && <span> · <span className="text-danger font-medium">{summary.failed} failed</span> — fix and save again</span>}</span>
                : <span><span className="font-medium text-text">{readyToSave.length}</span> ready to save{needAttention.length > 0 && <span> · <span className="text-warn">{needAttention.length}</span> need attention (skipped)</span>}{unattachedBacks.length > 0 && <span> · <span className="text-warn">{unattachedBacks.length}</span> back{unattachedBacks.length === 1 ? "" : "s"} unattached (ignored)</span>}{skippedDupes.length > 0 && <span> · <span className="text-warn">{skippedDupes.length}</span> held back (skipped)</span>}</span>}
            </div>
            <div className="flex items-center gap-2">
              {summary && summary.failed === 0 && (
                <Button variant="secondary" onClick={() => navigate(backLink)}>Done — view licences</Button>
              )}
              <Button icon={Save} onClick={saveAll} disabled={saving || busy || readyToSave.length === 0}>
                {saving ? "Saving…" : `Save ${readyToSave.length} licence${readyToSave.length === 1 ? "" : "s"}`}
              </Button>
            </div>
          </div>
        </StickyBottomBar>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Small bits
// -----------------------------------------------------------------------------
function Chip({ tone, children }: { tone: "muted" | "ok" | "warn" | "info" | "danger"; children: ReactNode }) {
  const cls = {
    muted:  "border-line text-text-muted bg-ink-raised",
    ok:     "border-ok/30 text-ok bg-ok/5",
    warn:   "border-warn/30 text-warn bg-warn/5",
    info:   "border-info/30 text-info bg-info/5",
    danger: "border-danger/30 text-danger bg-danger/5",
  }[tone];
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${cls}`}>{children}</span>;
}

function StatusChip({ status }: { status: RowStatus }) {
  switch (status) {
    case "queued":     return <Chip tone="muted">queued</Chip>;
    case "uploading":  return <Chip tone="info"><Spinner size={11} /> uploading</Chip>;
    case "reading":    return <Chip tone="info"><Sparkles size={11} /> reading</Chip>;
    case "ready":      return <Chip tone="ok">read — check it</Chip>;
    case "error":      return <Chip tone="danger">couldn't read</Chip>;
    case "back":       return <Chip tone="info">back side</Chip>;
    case "saving":     return <Chip tone="info"><Spinner size={11} /> saving</Chip>;
    case "saved":      return <Chip tone="ok"><CheckCircle2 size={11} /> saved</Chip>;
    case "save_error": return <Chip tone="danger">save failed</Chip>;
  }
}
